/**
 * Centralized post-authentication routing decision.
 *
 * Called after a successful login, TOTP setup, or TOTP verification
 * to decide whether the user lands on the Dashboard, resumes a
 * partially-completed new-flow onboarding wizard, or is kicked into
 * the legacy onboarding carousel.
 *
 * onboardingVersion semantics:
 *   0 → legacy flow (existing users) — use the original logic
 *   1 → new wizard flow (Phase 1+ signups) — route to wizard unless complete
 *   2 → letter-first flow — route into the letter stack unless complete
 */

export type PostAuthUser = {
  onboardingVersion?: number | null;
  onboardingStep?: string | null;
};

export type PostAuthContext = {
  /** Result of any existing legacy-flow check (`trustData.isOnboardingCompleted` etc.) */
  legacyOnboardingCompleted: boolean;
};

export type PostAuthTarget =
  | { kind: 'dashboard' }
  | { kind: 'wizard'; initialStep: string }
  | { kind: 'letterFlow'; initialStep: string }
  | { kind: 'legacyOnboardingSplash' };

/**
 * Decide where a freshly-authenticated user should land.
 */
export function pickPostAuthTarget(
  user: PostAuthUser | null | undefined,
  ctx: PostAuthContext,
): PostAuthTarget {
  const version = user?.onboardingVersion ?? 0;
  const step = user?.onboardingStep ?? null;

  // Letter-first flow — version 2 signups
  if (version >= 2) {
    if (step === 'complete') return { kind: 'dashboard' };
    // Resume where they left off: the hook, or the Levels screen if
    // they already declined the letter ("Not right now").
    return { kind: 'letterFlow', initialStep: step ?? 'letter' };
  }

  // New wizard flow — only users flagged as version 1
  if (version >= 1) {
    if (step === 'complete') return { kind: 'dashboard' };
    // Users mid-wizard: resume at their saved step (or start at 'profile' if null)
    return { kind: 'wizard', initialStep: step ?? 'profile' };
  }

  // Legacy flow — existing users fall through to the old behavior
  return ctx.legacyOnboardingCompleted
    ? { kind: 'dashboard' }
    : { kind: 'legacyOnboardingSplash' };
}

/**
 * Map a wizard resume step name to the matching screen name in
 * WizardNavigator. Unknown / missing steps start from Profile.
 */
export function wizardScreenForStep(step: string): string {
  switch (step) {
    case 'profile':          return 'WizardProfile';
    case 'household':        return 'WizardHousehold';
    case 'roles_intro':      return 'WizardRolesIntro';
    case 'professionals':    return 'WizardProfessionals';
    case 'documents':        return 'WizardDocuments';
    case 'successor':        return 'WizardSuccessorSoftAsk';
    default:                 return 'WizardProfile';
  }
}

/**
 * Map a letter-flow resume step to the matching screen name in
 * LetterFirstNavigator. Unknown / missing steps start from the hook.
 */
export function letterScreenForStep(step: string): string {
  switch (step) {
    case 'letter': return 'LetterHook';
    case 'levels': return 'LetterLevels';
    default:       return 'LetterHook';
  }
}
